import React from 'react';
import { Alert, Select, TextInput, Textarea } from '@parsina/framework';
import { bffMutation, bffQuery, useClinicalTranslation } from '@parsina/framework/clinical';

import { useTranslation } from '../../i18n';
import { COUNTRIES, countryForPhone, countryName } from '../../constants/countries';
import { StepHeading, PrimaryAction } from '../../components/auth/OnboardingParts.jsx';

/**
 * «مشخصات پزشک» for a doctor licensed OUTSIDE Iran (PM-0061).
 *
 * The sibling of DoctorIdentityStep, and like it a PORT of the doctor panel's
 * screen (doctor-app src/screens/Onboarding, `StepForeignInfo`) — change it
 * there first. A doctor who signed in with a non-Iranian number holds no
 * کد نظام پزشکی, so there is nothing to look up and nothing to read: this is the
 * one form on which a doctor TYPES their professional identity. Name,
 * specialty, degree, country and city are required; the licence number their
 * own country issued is optional. Nothing is verified, and the form says so
 * before the doctor saves rather than after.
 *
 * ── Same transport as its sibling ──────────────────────────────────────────
 *
 * `completeForeignOnboarding` lives in the doctor BFF, reached over the
 * CLINICAL transport. WHO may call it is that BFF's decision, read from its own
 * record of the number — DoctorTrack only renders this form on a positive
 * `onboardingContext`, and rendering it for anyone else would earn a refusal.
 *
 * ── Two catalogues ─────────────────────────────────────────────────────────
 *
 * The words shared with the council step (`auth.onboarding.professional`,
 * `.saving`, `.next`, the bio label) are the clinical pack's. The form's own
 * words are this panel's (`auth.onboarding.foreign.*`) until the kit's pack
 * carries them.
 *
 * ── The specialty is picked, not typed (PM-0074) ───────────────────────────
 *
 * From the practitioner service's trilingual catalogue (`specialties`, doctor
 * BFF), labelled in the panel's language. The form sends the row's CODE and
 * the BFF stores its Persian name; «سایر» opens a text box for a specialty the
 * list does not name. The kit's Select has no option groups, so unlike the
 * doctor panel the list here is one alphabetical run. A BFF without the query
 * leaves the old free-text box in place.
 *
 * French (PM-0185) asks for `nameFr` too. A doctor BFF from before that field
 * refuses the whole query, so the form asks again without it — the picker stays,
 * and a French reader reads the English names — and only a BFF without the
 * query at all leaves the free-text box. German (PM-0212) adds `nameDe` the same
 * way, one step earlier: a BFF with `nameFr` but not `nameDe` is asked without
 * `nameDe` first, and a German reader reads the English names.
 */

const COMPLETE_FOREIGN = `
  mutation CompleteForeignOnboarding($input: ForeignOnboardingInput!) {
    completeForeignOnboarding(input: $input) {
      id fullName medicalCode specialty degree country city selfDeclared bio
    }
  }
`;

const SPECIALTIES = `
  query Specialties {
    specialties { code kind nameFa nameEn nameAr nameFr nameDe sortOrder }
  }
`;

// The same query for a doctor BFF that predates `nameDe` (see the header).
const SPECIALTIES_BEFORE_DE = `
  query Specialties {
    specialties { code kind nameFa nameEn nameAr nameFr sortOrder }
  }
`;

// The same query for a doctor BFF that predates `nameFr` (see the header).
const SPECIALTIES_BEFORE_FR = `
  query Specialties {
    specialties { code kind nameFa nameEn nameAr sortOrder }
  }
`;

// Must match the doctor BFF's OTHER_SPECIALTY_CODE.
const OTHER_SPECIALTY = 'other';

// The row's fields a reader of each language is labelled from, in order. Persian
// is the last resort for every one — and French and German read English before
// it (docs/locale/FRENCH.md D4, PM-0212). A language not listed reads Persian.
const SPECIALTY_FIELDS = {
  en: ['nameEn'],
  ar: ['nameAr'],
  fr: ['nameFr', 'nameEn'],
  de: ['nameDe', 'nameEn'],
};

const specialtyLabel = (row, locale) =>
  (SPECIALTY_FIELDS[locale] || []).map((field) => row[field]).find(Boolean) || row.nameFa;

const REQUIRED = ['fullName', 'degree', 'country', 'city'];

export default function DoctorForeignStep({ phone, onDone, onSkip, disabled = false }) {
  const { t: ct } = useClinicalTranslation();
  const { t, locale } = useTranslation();

  const [form, setForm] = React.useState(() => ({
    // `specialtyCode` is a catalogue row's code, or OTHER_SPECIALTY with the
    // doctor's own words in `specialty`.
    fullName: '', specialtyCode: '', specialty: '', degree: '',
    // The country of the number they signed in with is the likeliest issuer of
    // their licence, so it is offered — and stays theirs to change.
    country: countryForPhone(phone)?.iso2 || '',
    city: '', licenseNumber: '',
  }));
  const [bio, setBio] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  // `null` = loading; `[]` = unavailable → the free-text box, never a block.
  const [catalogue, setCatalogue] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    bffQuery(SPECIALTIES)
      .catch(() => bffQuery(SPECIALTIES_BEFORE_DE))
      .catch(() => bffQuery(SPECIALTIES_BEFORE_FR))
      .then((data) => { if (alive) setCatalogue(data?.specialties || []); })
      .catch(() => { if (alive) setCatalogue([]); });
    return () => { alive = false; };
  }, []);
  const pickerless = Array.isArray(catalogue) && catalogue.length === 0;
  const typing = pickerless || form.specialtyCode === OTHER_SPECIALTY;

  const specialtyOptions = React.useMemo(
    () => [
      ...(catalogue || [])
        .map((r) => ({ value: r.code, label: specialtyLabel(r, locale) }))
        .sort((a, b) => a.label.localeCompare(b.label, locale)),
      { value: OTHER_SPECIALTY, label: t('auth.onboarding.foreign.specialtyOther') },
    ],
    [catalogue, locale, t],
  );

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const specialtyGiven = typing ? form.specialty.trim() !== '' : form.specialtyCode !== '';
  const complete = specialtyGiven && REQUIRED.every((name) => form[name].trim() !== '');

  const countryOptions = React.useMemo(
    () => COUNTRIES
      .map((c) => ({ value: c.iso2, label: countryName(c, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale)),
    [locale],
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (saving || disabled) return;
    if (!complete) { setError(t('auth.onboarding.foreign.incomplete')); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await bffMutation(COMPLETE_FOREIGN, {
        input: {
          fullName: form.fullName.trim(),
          // A picked row sends only its code — the BFF stores its Persian name.
          ...(pickerless
            ? { specialty: form.specialty.trim() }
            : {
              specialtyCode: form.specialtyCode,
              specialty: typing ? form.specialty.trim() : undefined,
            }),
          degree: form.degree.trim(),
          country: form.country,
          city: form.city.trim(),
          licenseNumber: form.licenseNumber.trim() || undefined,
          bio: bio.trim() || undefined,
        },
      });
      const doctor = res?.completeForeignOnboarding;
      if (!doctor?.id) throw new Error(ct('errors.save.doctorProfile'));
      // Awaited, as on the council step: the caller asks the BFF one more
      // question before it decides which phase comes next.
      await onDone({ doctor });
    } catch (err) {
      const failed = err?.extensions?.code;
      if (failed === 'DUPLICATE_LICENSE_NUMBER') setError(t('auth.onboarding.foreign.licenseTaken'));
      else if (failed === 'FOREIGN_PROFILE_INCOMPLETE') setError(t('auth.onboarding.foreign.incomplete'));
      else if (failed === 'FOREIGN_ONBOARDING_NOT_ALLOWED') setError(t('auth.onboarding.foreign.notAllowed'));
      else if (failed === 'SPECIALTY_UNKNOWN') setError(t('auth.onboarding.foreign.specialtyUnknown'));
      else setError(err.message || ct('errors.save.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="onboarding-doctor-step" data-foreign="true">
      <StepHeading
        title={ct('auth.onboarding.professional')}
        subtitle={t('auth.onboarding.foreign.prompt')}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <TextInput
          label={t('auth.onboarding.foreign.fullName')}
          required
          value={form.fullName}
          onChange={set('fullName')}
          dir="auto"
          autoComplete="name"
          maxLength={120}
          disabled={saving}
          data-testid="onboarding-foreign-fullName"
        />
        {pickerless ? null : (
          <Select
            label={t('auth.onboarding.foreign.specialty')}
            required
            value={form.specialtyCode}
            onChange={set('specialtyCode')}
            options={specialtyOptions}
            placeholder={t('auth.onboarding.foreign.specialtyChoose')}
            disabled={saving || catalogue === null}
            data-testid="onboarding-foreign-specialty-select"
          />
        )}
        {typing ? (
          <TextInput
            label={t(pickerless ? 'auth.onboarding.foreign.specialty' : 'auth.onboarding.foreign.specialtyOtherLabel')}
            required
            value={form.specialty}
            onChange={set('specialty')}
            placeholder={t('auth.onboarding.foreign.specialtyPlaceholder')}
            dir="auto"
            maxLength={120}
            disabled={saving}
            data-testid="onboarding-foreign-specialty"
          />
        ) : null}
        <TextInput
          label={t('auth.onboarding.foreign.degree')}
          required
          value={form.degree}
          onChange={set('degree')}
          placeholder={t('auth.onboarding.foreign.degreePlaceholder')}
          dir="auto"
          maxLength={120}
          disabled={saving}
          data-testid="onboarding-foreign-degree"
        />
        <Select
          label={t('auth.onboarding.foreign.country')}
          required
          value={form.country}
          onChange={set('country')}
          options={countryOptions}
          placeholder={t('auth.onboarding.foreign.countryPlaceholder')}
          disabled={saving}
          data-testid="onboarding-foreign-country"
        />
        <TextInput
          label={t('auth.onboarding.foreign.city')}
          required
          value={form.city}
          onChange={set('city')}
          dir="auto"
          maxLength={80}
          disabled={saving}
          data-testid="onboarding-foreign-city"
        />
        <TextInput
          label={t('auth.onboarding.foreign.license')}
          value={form.licenseNumber}
          onChange={set('licenseNumber')}
          helperText={t('auth.onboarding.foreign.licenseHint')}
          dir="ltr"
          maxLength={40}
          disabled={saving}
          data-testid="onboarding-foreign-license"
        />

        <Textarea
          label={ct('auth.onboarding.bioOptional')}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={3}
          disabled={saving}
          data-testid="onboarding-bio"
        />

        {/* Said BEFORE the save: the doctor should know their profile is
            admitted on their word, not learn it from a badge afterwards. */}
        <Alert variant="warning" data-testid="onboarding-foreign-notice">
          {t('auth.onboarding.foreign.notice')}
        </Alert>

        {error ? <Alert variant="error" data-testid="onboarding-doctor-error">{error}</Alert> : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          {/* Leaving the doctor track is always available — see DoctorIdentityStep. */}
          <PrimaryAction secondary type="button" onClick={onSkip} disabled={saving} data-testid="onboarding-doctor-skip">
            {ct('common.actions.back')}
          </PrimaryAction>
          <PrimaryAction
            fullWidth
            type="submit"
            disabled={saving || disabled || !complete}
            data-testid="onboarding-doctor-next"
          >
            {saving ? ct('auth.onboarding.saving') : ct('auth.onboarding.next')}
          </PrimaryAction>
        </div>
      </div>
    </form>
  );
}
